import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { UserCircle } from "@phosphor-icons/react";

const cache = new Map(); // storage path -> object URL (per session)

/**
 * Circular avatar showing the user's verified selfie. Falls back to a
 * gradient monogram (initials) when no selfie is available.
 */
export default function SelfieAvatar({
  path,
  name = "",
  size = 36,
  ring = true,
  className = "",
  testId,
}) {
  const [url, setUrl] = useState(() => (path ? cache.get(path) || null : null));
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!path || cache.has(path)) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API}/identity/file?path=${encodeURIComponent(path)}`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error("not-ok");
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        cache.set(path, objUrl);
        if (!cancelled) setUrl(objUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [path]);

  const initials =
    (name || "?")
      .split(/\s+/)
      .map((p) => p[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  const s = { width: size, height: size };
  const ringCls = ring
    ? "ring-2 ring-violet-400/40 ring-offset-2 ring-offset-transparent hover:ring-violet-400/70"
    : "";

  return (
    <div
      data-testid={testId}
      style={s}
      className={`relative rounded-full overflow-hidden border border-white/10 bg-gradient-to-br from-indigo-500/25 via-fuchsia-500/20 to-violet-500/25 grid place-items-center transition-shadow ${ringCls} ${className}`}
      aria-label={name ? `${name}'s selfie` : "Profile"}
      title={name || "Profile"}
    >
      {url && !failed ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : path && !failed ? (
        // Loading placeholder
        <UserCircle
          size={size * 0.65}
          weight="duotone"
          className="text-indigo-200/70 animate-pulse"
        />
      ) : (
        <span
          className="font-heading text-neutral-100"
          style={{ fontSize: size * 0.4 }}
        >
          {initials}
        </span>
      )}
    </div>
  );
}
